import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Timer, ArrowRight, Check } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../api";

export const eur = (cents) => (cents / 100).toLocaleString("it-IT", { style: "currency", currency: "EUR" });

// Server-authoritative Slot Qora: result, timer and persistence come from the backend (cookie-bound promo).
export default function SlotQora({ percentages = [5, 7, 10], onPromo }) {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [reels, setReels] = useState(["Q", "%", "Q"]);
  const [left, setLeft] = useState(0);
  const offset = useRef(0);

  const load = async () => { const { data } = await api.slotState(); setState(data); sync(data.promo); };
  const sync = (promo) => {
    if (!promo) return;
    offset.current = new Date(promo.server_now).getTime() - Date.now();
    onPromo?.(promo);
  };
  useEffect(() => { load(); api.billingEvent("slot_viewed").catch(() => {}); }, []); // eslint-disable-line

  useEffect(() => {
    const promo = state?.promo;
    if (!promo || promo.status !== "active") { setLeft(0); return; }
    const tick = () => {
      const s = Math.max(0, Math.round((new Date(promo.expires_at).getTime() - (Date.now() + offset.current)) / 1000));
      setLeft(s);
      if (s === 0) load();
    };
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, [state]); // eslint-disable-line

  const spin = async () => {
    if (spinning) return;
    setSpinning(true);
    const anim = setInterval(() => setReels([pick(), pick(), pick()]), 90);
    try {
      const [{ data }] = await Promise.all([api.slotSpin(), new Promise((r) => setTimeout(r, 2400))]);
      clearInterval(anim);
      setReels(["QORA", `-${data.promo.pct}%`, "QORA"]);
      setState((s) => ({ ...s, promo: data.promo, can_spin: false })); sync(data.promo);
    } catch (e) { clearInterval(anim); setReels(["Q", "%", "Q"]); alert(e?.response?.data?.detail || "Riprova"); }
    finally { setSpinning(false); }
  };
  const pick = () => { const o = ["QORA", ...percentages.map((p) => `-${p}%`)]; return o[Math.floor(Math.random() * o.length)]; };

  const promo = state?.promo;
  const active = promo && promo.status === "active" && left > 0;
  const mm = String(Math.floor(left / 60)).padStart(2, "0"), ss = String(left % 60).padStart(2, "0");

  return (
    <div className="rounded-3xl bg-[#0c2d34] text-white p-6 sm:p-10 relative overflow-hidden" data-testid="slot-qora">
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: "radial-gradient(60% 70% at 50% 0%, rgba(143,215,196,0.5), transparent 60%)" }} />
      <div className="relative grid lg:grid-cols-2 gap-8 items-center">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-bold text-[#8fd7c4]"><Sparkles className="h-3.5 w-3.5" /> Slot Qora</span>
          <h2 className="mt-4 font-display text-3xl md:text-4xl font-extrabold leading-tight">Sblocca il tuo sconto Qora</h2>
          <p className="mt-3 text-white/70 leading-relaxed">Ogni giocata sblocca uno sconto Qora dal {Math.min(...percentages)}% al {Math.max(...percentages)}%. Vale sul primo pagamento di qualsiasi piano, mensile o annuale, per 5 minuti reali dall'assegnazione.</p>
          <ul className="mt-4 space-y-1.5 text-sm text-white/80">
            {["Si vince sempre: la casualità riguarda solo l'importo", "Sconto applicato automaticamente al checkout, nessun codice", "Dal rinnovo successivo vale il prezzo di listino"].map((t) => <li key={t} className="flex gap-2"><Check className="h-4 w-4 text-[#8fd7c4] shrink-0 mt-0.5" />{t}</li>)}
          </ul>
        </div>
        <div className="rounded-3xl bg-white/5 border border-white/10 p-5 sm:p-6 text-center">
          <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="slot-reels">
            {reels.map((r, i) => (
              <div key={i} className={`h-20 sm:h-24 rounded-2xl grid place-items-center font-display font-extrabold text-xl sm:text-2xl ${i === 1 ? "bg-[#8fd7c4] text-[#0c2d34]" : "bg-white/10 text-white"} ${spinning ? "animate-pulse" : ""}`}>{r}</div>
            ))}
          </div>
          {!promo || promo.status === "expired" ? (
            <>
              <Button onClick={spin} disabled={spinning || !state?.can_spin} className="mt-5 h-14 w-full text-lg font-extrabold bg-white text-[#0c2d34] hover:bg-white/90" data-testid="slot-spin">{spinning ? "La slot sta girando…" : "GIRA"}</Button>
              {promo?.status === "expired" && <p className="mt-3 text-xs text-white/60" data-testid="slot-expired">La promozione precedente è scaduta. Puoi giocare di nuovo.</p>}
              {!promo && <p className="mt-3 text-xs text-white/60">Una giocata per sessione. Dopo l'accesso, una partecipazione per account.</p>}
            </>
          ) : (
            <div className="mt-5" data-testid="slot-result">
              <p className="font-display text-2xl sm:text-3xl font-extrabold" data-testid="slot-won">HAI SBLOCCATO -{promo.pct}%</p>
              {active ? (
                <>
                  <p className="text-sm text-white/70 mt-1">Riservalo e completa l'attivazione entro:</p>
                  <p className="font-display text-4xl font-extrabold tabular-nums mt-1" data-testid="slot-timer"><Timer className="inline h-6 w-6 mr-1 -mt-1" />{mm}:{ss}</p>
                  <Button onClick={() => navigate(`/checkout?plan=complete&interval=yearly`)} className="mt-4 h-12 w-full font-bold bg-[#8fd7c4] text-[#0c2d34] hover:bg-[#8fd7c4]/90" data-testid="slot-activate">Attiva Qora con -{promo.pct}% <ArrowRight className="ml-1 h-4 w-4" /></Button>
                  <p className="mt-2 text-[11px] text-white/50">Scegli il piano nel riepilogo: lo sconto viene applicato automaticamente.</p>
                </>
              ) : promo.status === "checkout" ? <p className="text-sm text-white/70 mt-2">Sconto riservato: hai una sessione di pagamento aperta.</p>
                : <p className="text-sm text-white/70 mt-2">Sconto già utilizzato.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
